// Function index:
//   buildCast(combat) — CombatSnapshot → EncounterCast: order_idx order,
//       party/ally→'party' enemy→'foe', class→body, equipped names→weapon/
//       shield/ranged via the parts keyword maps, archetype→creature.
//       Missing cosmetics degrade to generic/fallback parts — the window
//       must render something sensible for pre-enrichment snapshots.

import type { CombatSnapshot } from '../api.js';
import { bodyForClass } from './parts/bodies.js';
import { creatureForArchetype } from './parts/creatures.js';
import { weaponForItemName, RANGED_WEAPONS } from './parts/weapons.js';
import type { EncounterCast, EncounterFigure } from './types.js';

export function buildCast(combat: CombatSnapshot): EncounterCast {
  const figures: EncounterFigure[] = [...combat.participants]
    .sort((a, b) => a.order_idx - b.order_idx)
    .map((p) => {
      const isCharacter = p.kind === 'character';
      // NPCs carry their real loadout's wielded names since the WYSIWYG-loot
      // pass — the same keyword map that arms characters arms foes.
      const weapon = weaponForItemName(p.weapon_name);
      return {
        key: isCharacter ? `char:${p.character_id}` : `npc:${p.npc_id}`,
        name: p.name,
        side: p.side === 'enemy' ? 'foe' : 'party',
        kind: p.kind,
        hp: p.hp,
        max_hp: p.max_hp,
        defeated: p.defeated,
        body: isCharacter ? bodyForClass(p.character_class) : null,
        creature: isCharacter ? null : creatureForArchetype(p.archetype),
        weapon,
        shield: weaponForItemName(p.offhand_name) === 'shield',
        ranged: weapon !== null && RANGED_WEAPONS.has(weapon),
        mounted: isCharacter && p.mounted === true,
      };
    });
  return { figures, wagon: combat.wagon === true };
}
